import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Pencil, X } from 'lucide-react';
import { api } from '../../api/client';
import { formatInstant } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import type { JobCasingString } from '../../types/casing-string';
import type {
  CasingCorrectionBody,
  CasingStringValues,
  CasingValueKey,
} from '../../types/casing-string-correction';

/**
 * A hand CORRECTS one casing string's spec, and sees who wrote the one on file.
 *
 * ─── WHY A FIELD HAND CAN OVERWRITE THIS AT ALL ────────────────────────────
 *
 * The tenant: the spec is not only sometimes missing, it is sometimes WRONG.
 * The company man or dispatch gives the wrong pipe, last-minute pipe changes
 * happen, and the crew on location is who verifies it. The torque window this
 * card shows is resolved from these four values — on TNT J-4290's string 2, a
 * grade of L-80 on P-110 pipe resolves to a 4,930 ft-lb optimum against a true
 * minimum of 5,010. So the correction lives on the card whose number it moves.
 *
 * ─── `expected` IS CAPTURED WHEN THE FORM OPENS ────────────────────────────
 *
 * The API writes only if the string still holds the six values the hand was
 * looking at. Those are snapshotted on OPEN, not read from props at submit —
 * a refetch while the form is open would otherwise swap in the new values as
 * `expected` and let the hand's stale edits overwrite a change they never saw,
 * which is precisely the case the 409 exists for. On a 409 the form closes and
 * the job refetches, so the next open starts from what is actually on file.
 *
 * ─── ONLINE ONLY ───────────────────────────────────────────────────────────
 *
 * Plain useMutation, disabled offline. A queued correction replayed later would
 * be compared against a spec the office may have changed in the meantime, and
 * the hand who confirmed the pipe would no longer be standing beside it.
 */

const FIELDS: { key: CasingValueKey; label: string; maxLength: number }[] = [
  { key: 'size_inches', label: 'Size (in)', maxLength: 20 },
  { key: 'weight_lb_ft', label: 'Weight (lb/ft)', maxLength: 20 },
  { key: 'thread_type', label: 'Connection', maxLength: 200 },
  { key: 'grade', label: 'Grade', maxLength: 200 },
  { key: 'size_mm', label: 'Size (mm)', maxLength: 20 },
  { key: 'weight_kg_m', label: 'Weight (kg/m)', maxLength: 20 },
];

const SOURCE_LABEL: Record<'office' | 'field' | 'ai', string> = {
  office: 'office',
  field: 'field',
  ai: 'AI assistant',
};

/** The six values as the screen shows them. Blank folds to null, as the API folds it. */
export function valuesOf(s: JobCasingString): CasingStringValues {
  const v = (x: string | null | undefined) => (x == null || x.trim() === '' ? null : x);
  return {
    size_inches: v(s.size_inches),
    size_mm: v(s.size_mm),
    weight_lb_ft: v(s.weight_lb_ft),
    weight_kg_m: v(s.weight_kg_m),
    grade: v(s.grade),
    thread_type: v(s.thread_type),
  };
}

/**
 * The attribution line. Never blank: a string written before the API recorded
 * attribution says so, rather than rendering an empty "Changed by".
 */
export function attributionText(s: JobCasingString, timeZone: string | null): string {
  if (!s.source) return 'No change record for this spec';
  // When it changed, on the TENANT's clock (companies.time_zone). An unknown
  // zone drops the time from the line rather than guessing one.
  const when = formatInstant(s.updated_at ?? null, timeZone);
  const who = s.updated_by_name?.trim() || null;
  const door = SOURCE_LABEL[s.source];
  const head = who ? `Changed by ${who} (${door})` : `Changed in the ${door}`;
  return when ? `${head} · ${when}` : head;
}

/**
 * Only the keys the hand actually changed — trimmed, blank sent as null. An
 * untouched field is not sent, so the API keeps it.
 */
export function changedValues(
  expected: CasingStringValues,
  inputs: Record<CasingValueKey, string>,
): Partial<CasingStringValues> {
  const out: Partial<CasingStringValues> = {};
  for (const { key } of FIELDS) {
    const typed = inputs[key].trim();
    const next = typed === '' ? null : typed;
    if (next !== expected[key]) out[key] = next;
  }
  return out;
}

interface Props {
  jobId: string;
  string: JobCasingString;
  isOnline: boolean;
  /** Called after a correction or a 409, so the job refetches. */
  onChanged: () => void;
}

export default function CasingStringCorrection({ jobId, string: s, isOnline, onChanged }: Props) {
  const timeZone = useTenantTimeZone();
  const [expected, setExpected] = useState<CasingStringValues | null>(null);
  const [inputs, setInputs] = useState<Record<CasingValueKey, string>>({
    size_inches: '', size_mm: '', weight_lb_ft: '', weight_kg_m: '', grade: '', thread_type: '',
  });
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const open = () => {
    const seen = valuesOf(s);
    setExpected(seen);
    setInputs({
      size_inches: seen.size_inches ?? '', size_mm: seen.size_mm ?? '',
      weight_lb_ft: seen.weight_lb_ft ?? '', weight_kg_m: seen.weight_kg_m ?? '',
      grade: seen.grade ?? '', thread_type: seen.thread_type ?? '',
    });
    setReason('');
    setError(null);
    setNotice(null);
  };
  const close = () => setExpected(null);

  const changes = expected ? changedValues(expected, inputs) : {};
  const nothingChanged = Object.keys(changes).length === 0;

  const mutation = useMutation({
    mutationFn: () => {
      const body: CasingCorrectionBody = { expected: expected!, reason: reason.trim(), ...changes };
      return api.employee.correctCasingString(jobId, s.string_number, body);
    },
    onSuccess: (res) => {
      const data = res.data?.data;
      close();
      if (data && !data.changed) {
        setNotice('That is already the spec on file — nothing was changed.');
      } else {
        const openTickets = data?.unfrozen_tickets?.length ?? 0;
        setNotice(
          'Spec corrected. The office has been notified.' +
          (openTickets > 0
            ? ` ${openTickets} open ${openTickets === 1 ? 'ticket was' : 'tickets were'} on the old spec; the office will review ${openTickets === 1 ? 'it' : 'them'}.`
            : ''),
        );
      }
      onChanged();
    },
    onError: (err: any) => {
      const httpStatus = err?.response?.status;
      if (httpStatus === 404 && !err?.response?.data?.field) {
        setError("You're not on this job's crew, so you can't correct its casing spec. Ask dispatch to add you to the crew.");
        return;
      }
      // The API's sentence, verbatim: a 409 names what moved and who moved it,
      // a 400 names the field. Nothing written here would be as useful.
      setError(err?.response?.data?.error || err?.message || 'Could not save the correction. Try again.');
      if (httpStatus === 409) {
        // The form was built on a spec that is no longer on file. Close it and
        // refetch, so the next attempt starts from what is actually there.
        close();
        onChanged();
      }
    },
  });

  return (
    <div className="space-y-2">
      <p className="text-fieldiq-muted text-xs">{attributionText(s, timeZone)}</p>

      {!expected && isOnline && (
        <button
          type="button"
          onClick={open}
          className="flex items-center gap-2 text-fieldiq-accent text-sm font-medium"
        >
          <Pencil size={14} /> Correct this spec
        </button>
      )}

      {!expected && !isOnline && (
        <p className="text-fieldiq-muted text-xs flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
          You're offline — a casing spec can't be corrected until you reconnect.
        </p>
      )}

      {expected && (
        <div className="bg-fieldiq-dark border border-fieldiq-border rounded-lg p-3 space-y-3">
          <p className="text-amber-400 text-xs flex items-start gap-2">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            Correct this only after confirming the pipe on location. The makeup torque on this card is
            recalculated from these values, and the office is told.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(({ key, label, maxLength }) => (
              <div key={key}>
                <label htmlFor={`casing-${s.string_number}-${key}`} className="text-fieldiq-muted text-xs block mb-1">
                  {label}
                </label>
                <input
                  id={`casing-${s.string_number}-${key}`}
                  type="text"
                  value={inputs[key]}
                  maxLength={maxLength}
                  onChange={(e) => setInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                  className="w-full bg-fieldiq-card border border-fieldiq-border rounded px-3 py-2 text-white"
                />
              </div>
            ))}
          </div>
          <div>
            <label htmlFor={`casing-${s.string_number}-reason`} className="text-fieldiq-muted text-xs block mb-1">
              Why is it being corrected?
            </label>
            <textarea
              id={`casing-${s.string_number}-reason`}
              value={reason}
              maxLength={500}
              rows={2}
              placeholder="e.g. Company man confirmed L-80 on the pipe racks"
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-fieldiq-card border border-fieldiq-border rounded px-3 py-2 text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={!isOnline || nothingChanged || reason.trim() === '' || mutation.isPending}
              className="bg-fieldiq-accent text-fieldiq-dark font-medium rounded px-4 py-2 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'Save correction'}
            </button>
            <button
              type="button"
              onClick={close}
              className="bg-fieldiq-card border border-fieldiq-border text-white rounded px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {notice && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-start gap-2">
          <CheckCircle size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-green-400 text-sm flex-1">{notice}</p>
          <button type="button" onClick={() => setNotice(null)} className="text-green-400" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-400 text-sm flex-1">{error}</p>
          <button type="button" onClick={() => setError(null)} className="text-red-400" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
